import { CapitalNeedInput } from '#/graphql/types';
import Papa from 'papaparse';

const YEAR_PATTERN = /^\d{4}$/;

// Strips currency symbols/thousand separators (e.g. "$14,269.49", " $-   ") down to a number.
// Negatives can appear as a leading "-" (e.g. "-15.50") or accounting-style parentheses (e.g. "$(14,269.49)").
const parseCurrency = (value: string): number => {
  const isNegative = /-|\([^()]*\)/.test(value);
  const numeric = Number(value.replace(/[^0-9.]/g, ''));

  if (Number.isNaN(numeric)) {
    return 0;
  }

  return isNegative ? -numeric : numeric;
};

export const processExcel = (tableContent: string): CapitalNeedInput[] => {
  // Excel clipboard data is tab-delimited; PapaParse handles quoted cells with
  // embedded tabs/newlines that a naive split('\t') would break on.
  const { data } = Papa.parse<string[]>(tableContent, { delimiter: '\t', skipEmptyLines: true });
  const rows = data.filter((cells) => cells.some((cell) => cell.trim() !== ''));

  // The "Item/Scope" header row is identified by its year columns (e.g. 2023, 2024, ...).
  const headerRowIndex = rows.findIndex(
    (cells) => cells.filter((cell) => YEAR_PATTERN.test(cell.trim())).length >= 2,
  );

  if (headerRowIndex === -1) {
    return [];
  }

  const yearColumns = rows[headerRowIndex]
    .map((cell, index) => ({ year: cell.trim(), index }))
    .filter(({ year }) => YEAR_PATTERN.test(year));

  const result: CapitalNeedInput[] = [];

  for (let i = headerRowIndex + 1; i < rows.length; i += 1) {
    const cells = rows[i];
    const title = cells[0]?.trim();

    if (!title || /total/i.test(title)) {
      continue;
    }

    yearColumns.forEach(({ year, index }) => {
      const cost = parseCurrency(cells[index] ?? '');

      if (cost > 0) {
        result.push({
          title,
          plannedDate: `${year}-12-31`,
          cost,
          assetId: '',
        });
      }
    });
  }

  return result;
};
